import {
  useState,
  FocusEvent,
  ChangeEvent,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { ITopic, ITopicGroup, MessageType, TNullable, TopicStatus } from '@common/types';
import { Textarea } from '@components/ui/textarea';
import { QUESTION_QUERY_MIN_LENGTH } from '@constants/app';
import { Pages } from '@constants/pages';
import useChat from '@hooks/use-chat';
import {
  cn,
  findFirstTopicGroupInQuery,
  findFirstTopicInQuery,
  getExactGroupFromTopicGroupsList,
  getExactTopicFromTopicsList,
  getOnlyDisabledTopicFromGroup,
  normalizeQueryForTopicMatching,
  QUERY_PARAM,
  SUBMIT_QUERY_PARAM,
  shouldDisableSearchFieldAutoFocus,
  shouldUseMaxHight,
  shouldUseSessionStorage,
  trimQuery,
} from '@lib/utils';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface IQuestionInputProps {
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  topics: ITopic[];
  topicGroups: ITopicGroup[];
  setTopicGroup: Dispatch<SetStateAction<TNullable<ITopicGroup>>>;
  setQueryIncludesTopicGroup: Dispatch<SetStateAction<boolean>>;
  addStaticMessage: (messages: string, type: MessageType) => void;
  setShowTopicsError: Dispatch<SetStateAction<boolean>>;
  sendQuery: (
    query: string,
    topicId: string,
    staticResponseMessage?: string,
    staticResponseType?: MessageType
  ) => Promise<void>;
  clearPendingQueryLog: () => Promise<void>;
  ensurePendingQueryLog: (query: string) => Promise<void>;
  disabled: boolean;
}

function QuestionInput(props: IQuestionInputProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    agreedToDisclaimer,
    agreedToDisclaimerPerSession,
    setShowAgreementBanner,
    selectedTopic,
    setSelectedTopic,
  } = useChat();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!shouldDisableSearchFieldAutoFocus() && !props.disabled) {
      textareaRef.current?.focus();
    }
  }, [props.disabled]);

  useEffect(() => {
    const initialQuery = searchParams.get(QUERY_PARAM);
    const shouldSubmitQuery = searchParams.get(SUBMIT_QUERY_PARAM) === 'true';
    if (initialQuery && initialQuery.length > 0 && props.topics.length > 0) {
      props.setQuery(initialQuery);
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.delete(QUERY_PARAM);
      updatedSearchParams.delete(SUBMIT_QUERY_PARAM);
      setSearchParams(updatedSearchParams);
      if (shouldSubmitQuery) {
        void handleSubmit(initialQuery);
      }
    }
  }, [props.topics]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [props.query]);

  function handleFocus() {
    setIsFocused(true);
  }

  function handleBlurTextarea(e: FocusEvent<HTMLTextAreaElement>) {
    if (!e.target.value) {
      setIsFocused(false);
    }
  }

  function handleChangeTextarea(e: ChangeEvent<HTMLTextAreaElement>) {
    props.setQuery(e.target.value);
  }

  function handleEnter(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && props.query) {
      e.preventDefault();
      void handleSubmit();
    }
  }

  async function handleSubmit(initialQuery: string = '') {
    const submittedQuery = initialQuery || props.query;
    if (
      (shouldUseSessionStorage() && !agreedToDisclaimerPerSession) ||
      (!shouldUseSessionStorage() && !agreedToDisclaimer)
    ) {
      if (shouldUseMaxHight()) {
        const params = new URLSearchParams({
          [QUERY_PARAM]: submittedQuery,
          [SUBMIT_QUERY_PARAM]: 'true',
        });
        navigate(`${Pages.Disclaimer}?${params.toString()}`);
      } else {
        setShowAgreementBanner(true);
      }
      return;
    }
    const queryTrimmed = trimQuery(submittedQuery);
    if (props.topics.length === 0) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      props.addStaticMessage(t('common.general_error'), MessageType.Error);
      props.setQuery('');
      return;
    }
    if (queryTrimmed === '') {
      return;
    }
    if (queryTrimmed.length < QUESTION_QUERY_MIN_LENGTH) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      props.addStaticMessage(
        t('chat.response.too_short', { count: QUESTION_QUERY_MIN_LENGTH }),
        MessageType.Message
      );
      props.setQuery('');
      return;
    }
    // Reset the state
    props.setTopicGroup(null);
    props.setQueryIncludesTopicGroup(false);

    const normalizedQuery = normalizeQueryForTopicMatching(queryTrimmed);

    // If query is an exact topic
    const foundTopic = getExactTopicFromTopicsList(props.topics, normalizedQuery);
    if (foundTopic) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      if (TopicStatus.Disabled === foundTopic.status) {
        props.addStaticMessage(
          t('chat.response.disabled_topic', { topic: foundTopic.displayName }),
          MessageType.DisabledTopicMessage
        );
        props.setQuery('');
        return;
      }
      setSelectedTopic(foundTopic.id);
      props.addStaticMessage(
        t('chat.response.topic_changed', { topic: foundTopic.displayName }),
        MessageType.Message
      );
      props.setQuery('');
      return;
    }
    // If query is an exact group
    if (props.topicGroups.length > 0) {
      const foundTopicGroup = getExactGroupFromTopicGroupsList(props.topicGroups, normalizedQuery);
      if (foundTopicGroup) {
        const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(props.topics, foundTopicGroup);
        if (disabledTopicFromGroup) {
          props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
          props.addStaticMessage(
            t('chat.response.disabled_topic', { topic: foundTopicGroup.name }),
            MessageType.DisabledTopicMessage
          );
          props.setQuery('');
          return;
        }
        await props.ensurePendingQueryLog(queryTrimmed);
        props.setTopicGroup(foundTopicGroup);
        props.setShowTopicsError(true);
        return;
      }
    }
    // If query includes a topic
    const firstTopic = findFirstTopicInQuery(normalizedQuery, props.topics);
    if (firstTopic) {
      if (TopicStatus.Disabled === firstTopic.status) {
        await props.sendQuery(
          queryTrimmed,
          firstTopic.id,
          t('chat.response.disabled_topic', { topic: firstTopic.displayName }),
          MessageType.DisabledTopicMessage
        );
        return;
      }
      setSelectedTopic(firstTopic!.id);
      await props.sendQuery(queryTrimmed, firstTopic!.id);
      props.setShowTopicsError(false);
      return;
    }
    // If query includes a group
    if (props.topicGroups.length > 0) {
      const firstTopicGroup = findFirstTopicGroupInQuery(normalizedQuery, props.topicGroups);
      if (firstTopicGroup) {
        const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(props.topics, firstTopicGroup);
        if (disabledTopicFromGroup) {
          await props.sendQuery(
            queryTrimmed,
            disabledTopicFromGroup.id,
            t('chat.response.disabled_topic', { topic: firstTopicGroup.name }),
            MessageType.DisabledTopicMessage
          );
          return;
        }
        await props.ensurePendingQueryLog(queryTrimmed);
        props.setTopicGroup(firstTopicGroup);
        props.setQueryIncludesTopicGroup(true);
        props.setShowTopicsError(true);
        return;
      }
    }

    if (selectedTopic) {
      const selectedTopicObject = props.topics.find((topic) => topic.id === selectedTopic);
      if (selectedTopicObject?.status === TopicStatus.Disabled) {
        props.addStaticMessage(
          t('chat.response.disabled_topic', { topic: selectedTopicObject.displayName }),
          MessageType.DisabledTopicMessage
        );
        props.setQuery('');
        return;
      }
      await props.sendQuery(queryTrimmed, selectedTopic);
      return;
    } else {
      await props.ensurePendingQueryLog(queryTrimmed);
      props.setShowTopicsError(true);
      return;
    }
  }

  return (
    <div className={cn('relative w-full mt-3', !shouldUseMaxHight() && 'mt-5')}>
      <label
        htmlFor="question-input"
        className={`absolute left-3 transition-all duration-200 ${
          isFocused || props.query
            ? `top-1 text-xs text-xircle-main ${props.disabled && '!text-slate-100'}`
            : 'top-1/2 -translate-y-1/2 text-gray-400'
        }`}
      >
        {t('chat.input.placeholder')}
      </label>
      <Textarea
        id="question-input"
        disabled={props.disabled}
        ref={textareaRef}
        value={props.query}
        onChange={handleChangeTextarea}
        onFocus={handleFocus}
        onBlur={handleBlurTextarea}
        onKeyDown={handleEnter}
        rows={1}
        className="pt-6 pb-2 px-3 pr-10 bg-white text-black min-h-14 text-base font-normal border-none resize-none overflow-hidden rounded-xl w-full focus:ring-0 focus:outline-none focus-visible:ring-0 query-input"
      />
      <Search
        className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 cursor-pointer"
        onClick={() => handleSubmit()}
      />
    </div>
  );
}

export default QuestionInput;
